import { useMemo, useState } from "react";
import type { WorkspaceSchema } from "@common/schemas/workspace.schema";
import { useWorkspacesList } from "@api/workspace.api";
import { useScope } from "@state/scopeContext.lib";
import { useWorkspaceEntities } from "@hooks/useWorkspaceEntities";
import { PreviewEntityScope } from "./previewEntityScope";

export type PreviewEntity = WorkspaceSchema["entities"][number];

export interface PreviewEntityGroup {
    workspaceId: string;
    clientName: string;
    entities: PreviewEntity[];
}

export interface TemplatePreviewTarget {
    /** Client-specific templates always preview their own client. */
    canSwitch: boolean;
    groups: PreviewEntityGroup[];
    selectedEntity: PreviewEntity | null;
    workspaceId: string | null;
    entityId: number | null;
    /** True while the clients or entities to pick from are still loading. */
    isLoading: boolean;
    selectEntity: (entityId: number) => void;
}

interface UseTemplatePreviewTargetParams {
    entityScope: PreviewEntityScope;
}

/**
 * Which entity, and so which client, the template preview reads data from.
 * The choice is local to the preview: it is never saved on the template.
 */
export const useTemplatePreviewTarget = ({
    entityScope: { isEntityIncluded },
}: UseTemplatePreviewTargetParams): TemplatePreviewTarget => {
    const { organizationId, workspaceId: scopeWorkspaceId } = useScope();
    const canSwitch = !scopeWorkspaceId;
    const { data: workspaces = [], isLoading: isWorkspacesLoading } =
        useWorkspacesList(
            organizationId
                ? { organizationId, includeEntities: true }
                : undefined,
            { enabled: canSwitch && !!organizationId },
        );
    const { entities: scopedEntities, isLoading: isScopedEntitiesLoading } =
        useWorkspaceEntities(scopeWorkspaceId ?? null);
    const [pickedEntityId, setPickedEntityId] = useState<number | null>(null);

    const groups = useMemo<PreviewEntityGroup[]>(
        () =>
            workspaces
                .map((workspace) => ({
                    workspaceId: workspace.id,
                    clientName: workspace.name,
                    entities: (workspace.entities ?? []).filter(
                        (entity) =>
                            !entity.isMockEntity && isEntityIncluded(entity),
                    ),
                }))
                .filter((group) => group.entities.length > 0),
        [workspaces, isEntityIncluded],
    );

    const selectedEntity = useMemo(() => {
        const entities = groups.flatMap((group) => group.entities);
        return (
            entities.find(({ id }) => id === pickedEntityId) ??
            entities[0] ??
            null
        );
    }, [groups, pickedEntityId]);

    const scopedEntity = useMemo(
        () => scopedEntities.find(isEntityIncluded) ?? null,
        [scopedEntities, isEntityIncluded],
    );

    return {
        canSwitch,
        groups,
        selectedEntity,
        workspaceId: scopeWorkspaceId ?? selectedEntity?.workspaceId ?? null,
        entityId: canSwitch
            ? (selectedEntity?.id ?? null)
            : (scopedEntity?.id ?? null),
        isLoading: canSwitch ? isWorkspacesLoading : isScopedEntitiesLoading,
        selectEntity: setPickedEntityId,
    };
};
